import { ExternalLink, Phone } from "lucide-react";
import type {
  TemplateButton as TemplateButtonType,
  TemplateComponents,
} from "@/lib/query/conversation/conversation.types";
import { cx } from "@/lib/style/cva.config";

interface TemplateMessageProps {
  body?: string;
  templateComponents?: TemplateComponents | null;
  isOutbound: boolean;
}

export function TemplateMessage({ body, templateComponents, isOutbound }: TemplateMessageProps) {
  const hasButtons = templateComponents?.buttons && templateComponents.buttons.length > 0;

  return (
    <div
      className={cx(
        "p-4",
        isOutbound
          ? "rounded-bl-xl rounded-tl-xl rounded-tr-xl border border-bordercolor-primary bg-brand-100"
          : "rounded-br-xl rounded-tl-xl rounded-tr-xl border-[0.5px] border-bordercolor-primary bg-surface-primary",
      )}
    >
      {/* Message body */}
      {body && (
        <p className="whitespace-pre-wrap text-[15px] font-medium leading-[1.6] text-txt-primary">
          {body}
        </p>
      )}

      {/* Render buttons if present */}
      {hasButtons && (
        <div
          className={cx(
            "mt-3 space-y-2 border-t border-bordercolor-primary pt-3",
            !body && "mt-0 border-t-0 pt-0",
          )}
        >
          {templateComponents.buttons!.map((button, index) => (
            <TemplateButton key={index} button={button} />
          ))}
        </div>
      )}
    </div>
  );
}

interface TemplateButtonProps {
  button: TemplateButtonType;
  className?: string;
}

function TemplateButton({ button, className }: TemplateButtonProps) {
  const handleClick = () => {
    if (button.type === "URL" && button.url) {
      // Check if it's a deep link to the app
      if (button.url.includes("/deeplink/")) {
        // For mobile app, deep links should open in a new tab/browser
        window.open(button.url, "_blank");
      } else {
        // External URL - open in browser
        window.open(button.url, "_blank");
      }
    } else if (button.type === "PHONE_NUMBER" && button.phoneNumber) {
      window.location.href = `tel:${button.phoneNumber}`;
    }
    // QUICK_REPLY buttons would send a message back (not implemented yet)
  };

  return (
    <button
      type="button"
      className={cx(
        "flex w-full items-center justify-center gap-2 rounded-md border border-bordercolor-primary bg-white px-4 py-3 text-sm font-semibold text-brand-500 transition-colors hover:bg-brand-50",
        className,
      )}
      onClick={handleClick}
    >
      {button.type === "URL" && <ExternalLink className="size-4" />}
      {button.type === "PHONE_NUMBER" && <Phone className="size-4" />}
      <span>{button.text}</span>
    </button>
  );
}
